'use client'

import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { PhoneInput } from '@/components/ui/phone-input'
import { Textarea } from '@/components/ui/textarea'
import type { Locale } from '@/i18n'
import { createCivilFormSchema, type CivilFormSchema } from '@/lib/validations/application'
import type { Vacancy } from '@/payload-types'
import { useApplicationStore } from '@/stores/applicationStore'
import { useApplicationModalStore } from '@/stores/applicationModalStore'

import { getCivilFormTranslations } from './constants'
import { trackCivilApplicationSubmit, trackApplicationFormError } from '@/utilities/analytics'

interface CivilFormProps {
  locale: Locale
  positionSlug?: string
}

export function CivilForm({ locale, positionSlug }: CivilFormProps) {
  const [mounted, setMounted] = useState(false)
  const store = useApplicationStore()
  const { closeModal } = useApplicationModalStore()
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submittedName, setSubmittedName] = useState<string>('')

  useEffect(() => {
    setMounted(true)
  }, [])

  const civilForm = mounted ? store.civilForm : {}
  const setCivilForm = store.setCivilForm

  const form = useForm<CivilFormSchema>({
    resolver: zodResolver(createCivilFormSchema(locale)),
    defaultValues: {
      position: '',
      fullName: '',
      age: undefined,
      hobbies: '',
      phone: '',
    },
  })

  useEffect(() => {
    if (mounted && civilForm) {
      Object.entries(civilForm).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          form.setValue(key as keyof CivilFormSchema, value as any)
        }
      })
    }
  }, [mounted, civilForm, form])

  useEffect(() => {
    if (positionSlug && !civilForm?.position) {
      const fetchVacancy = async () => {
        try {
          const response = await fetch(
            `/api/vacancies?where[slug][equals]=${positionSlug}&locale=${locale}`,
          )
          const data = await response.json()
          const vacancy = data.docs?.[0] as Vacancy | undefined
          if (vacancy?.title) {
            form.setValue('position', vacancy.title)
          }
        } catch (error) {
          console.error('Failed to fetch vacancy:', error)
        }
      }
      fetchVacancy()
    }
  }, [locale, positionSlug, civilForm?.position, form])

  const onSubmit = async (data: CivilFormSchema) => {
    setSubmitting(true)
    setSubmitError(null)
    setSubmitSuccess(false)

    try {
      const formData = {
        applicationType: 'civil',
        ...data,
      }

      const response = await fetch('/api/applications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.errors?.[0]?.message || 'Failed to submit application')
      }

      // Track successful submission
      trackCivilApplicationSubmit(data.position || positionSlug)

      setCivilForm(data as unknown as Partial<typeof civilForm>)
      setSubmittedName(data.fullName)
      setSubmitSuccess(true)
      form.reset()

      // Close modal after 2 seconds on success
      setTimeout(() => closeModal(), 2000)
    } catch (error) {
      console.error('Failed to submit application:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to submit application'
      setSubmitError(errorMessage)
      // Track form error
      trackApplicationFormError(errorMessage, 'civil')
    } finally {
      setSubmitting(false)
    }
  }

  const t = getCivilFormTranslations(locale)

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="bg-[#1a1a1a] rounded-lg p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="fullName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t.fullName} <span className="text-red-500">{t.required}</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder={t.fullNamePlaceholder} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t.phone} <span className="text-red-500">{t.required}</span>
                  </FormLabel>
                  <FormControl>
                    <PhoneInput placeholder={t.phonePlaceholder} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="age"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t.age}</FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      inputMode="numeric"
                      placeholder={t.agePlaceholder}
                      maxLength={3}
                      {...field}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '')
                        if (value === '') {
                          field.onChange(undefined)
                        } else {
                          const numValue = parseInt(value, 10)
                          if (numValue <= 100) {
                            field.onChange(numValue)
                          }
                        }
                      }}
                      value={field.value || ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="position"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t.position} <span className="text-red-500">{t.required}</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder={t.positionPlaceholder} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="hobbies"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t.hobbies}</FormLabel>
                <FormControl>
                  <Textarea placeholder={t.hobbiesPlaceholder} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {submitError && (
          <div className="bg-red-500 border border-red-500 text-red-100 rounded-lg p-4 font-medium">
            {submitError}
          </div>
        )}

        {submitSuccess && (
          <div className="bg-green-500 border border-green-500 text-green-100 rounded-lg p-4 font-medium">
            {locale === 'uk'
              ? `Заявку успішно надіслано, ${submittedName}! Ми зв'яжемося з вами найближчим часом.`
              : `Application submitted successfully, ${submittedName}! We will contact you soon.`}
          </div>
        )}

        <Button type="submit" size="lg" className="w-full" disabled={submitting || submitSuccess}>
          {submitSuccess
            ? locale === 'uk'
              ? 'Заявку надіслано ✓'
              : 'Application Sent ✓'
            : submitting
              ? locale === 'uk'
                ? 'Надсилання...'
                : 'Submitting...'
              : t.submit}
        </Button>
      </form>
    </Form>
  )
}
